import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormTextarea,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useSetEventCapture, useWaiveEventCapture } from "@/api/hooks/useEvents";
import {
  CAPTURE_KEYS,
  CAPTURE_KEY_LABELS,
  captureValue,
  type Event,
} from "@/api/types/event";

interface CaptureChecklistCardProps {
  event: Event;
}

/**
 * The event-day capture checklist — the playbook's five non-negotiables.
 * `delivered` is blocked server-side until every item is checked or the
 * whole checklist is explicitly waived with a reason.
 */
export function CaptureChecklistCard({ event }: CaptureChecklistCardProps) {
  const setCapture = useSetEventCapture();
  const waiveCapture = useWaiveEventCapture();
  const [waiveOpen, setWaiveOpen] = useState(false);

  async function toggle(key: (typeof CAPTURE_KEYS)[number], value: boolean) {
    try {
      await setCapture.mutateAsync({ id: event.id, key, value });
    } catch (err) {
      toastError("Couldn't update capture item", getErrorMessage(err));
    }
  }

  async function handleWaive(reason: string) {
    try {
      await waiveCapture.mutateAsync({ id: event.id, reason });
      toastSuccess("Capture checklist waived");
      setWaiveOpen(false);
    } catch (err) {
      toastError("Couldn't waive checklist", getErrorMessage(err));
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Capture checklist</CardTitle>
        {event.capture_waived ? (
          <Badge variant="warning">Waived</Badge>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => setWaiveOpen(true)}>
            Waive (reason)
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2" data-testid="capture-checklist">
        {CAPTURE_KEYS.map((key) => (
          <Checkbox
            key={key}
            id={`capture-${key}`}
            label={CAPTURE_KEY_LABELS[key]}
            checked={captureValue(event.capture_checklist, key)}
            disabled={event.capture_waived || setCapture.isPending}
            onChange={(e) => void toggle(key, e.target.checked)}
          />
        ))}
      </CardContent>

      <WaiveCaptureDialog
        open={waiveOpen}
        onCancel={() => setWaiveOpen(false)}
        onConfirm={handleWaive}
        isSubmitting={waiveCapture.isPending}
      />
    </Card>
  );
}

interface WaiveCaptureDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  isSubmitting: boolean;
}

function WaiveCaptureDialog({ open, onCancel, onConfirm, isSubmitting }: WaiveCaptureDialogProps) {
  const [reason, setReason] = useState("");

  return (
    <Dialog open={open} onClose={onCancel} ariaLabel="Waive capture checklist">
      <DialogContent size="sm">
        <DialogHeader onClose={onCancel}>Waive capture checklist</DialogHeader>
        <DialogBody>
          <FormTextarea
            label="Reason"
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. venue banned photography this event"
          />
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={() => onConfirm(reason)}
            disabled={!reason.trim() || isSubmitting}
          >
            {isSubmitting ? "Waiving..." : "Waive"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
